/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: ImportExtensionsModal.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Extensions module.
----------------------------------------------------------------*/

import { useState, useRef, useEffect } from 'react';
import { Upload, X, AlertCircle, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import { useLicenseStatus } from '../licensing';
import { ImportResultsPanel } from './components/ImportResultsPanel';

interface ImportExtensionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ImportResults {
  total: number;
  created: number;
  updated: number;
  skipped: number;
  deleted?: number;
  errors: string[];
  elapsedTime?: number; // Time in milliseconds
}

export default function ImportExtensionsModal({ isOpen, onClose, onSuccess }: ImportExtensionsModalProps) {
  const { t } = useI18n();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [csvContent, setCsvContent] = useState<string | null>(null);
  const [rowCount, setRowCount] = useState<number>(0);
  const [importMode, setImportMode] = useState<'skip' | 'update' | 'replace'>('skip');
  const [autoDetectedMode, setAutoDetectedMode] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<ImportResults | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [elapsedTime, setElapsedTime] = useState<number | null>(null);

  // License status for extension limit validation
  const { status: licenseStatus } = useLicenseStatus();

  // Calculate if import would exceed license limit
  const currentExtensions = licenseStatus?.extensions_used ?? 0;
  const maxExtensions = licenseStatus?.max_extensions ?? 12;
  const availableSlots = Math.max(0, maxExtensions - currentExtensions);
  const wouldExceedLimit = importMode !== 'update' && rowCount > availableSlots;
  const potentialTotal = currentExtensions + rowCount;

  // Reset modal state when it opens
  useEffect(() => {
    if (isOpen) {
      setSelectedFile(null);
      setCsvContent(null);
      setRowCount(0);
      setResults(null);
      setError(null);
      setImportMode('skip');
      setAutoDetectedMode(false);
      setIsProcessing(false);
      setElapsedTime(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      setError(t('extensions.import.invalidFileType'));
      return;
    }

    setSelectedFile(file);
    setError(null);
    setResults(null);

    // Read CSV file
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;

        // Validate CSV has content
        if (!content || content.trim().length === 0) {
          setError(t('extensions.import.invalidFileFormat'));
          setCsvContent(null);
          return;
        }

        // Count rows (excluding header)
        const lines = content.split('\n').filter(line => line.trim());
        const count = lines.length > 1 ? lines.length - 1 : 0;

        setRowCount(count);
        setCsvContent(content);

        // Auto-detect mode from the action column
        if (lines.length > 1) {
          const headerLine = lines[0].toLowerCase();
          const headers = headerLine.split(',').map(h =>
            h.trim().replace(/\s*\([^)]*\)\s*/g, '').replace(/\s+/g, '_').trim()
          );
          const actionIdx = headers.findIndex(h => h === 'action');

          if (actionIdx >= 0) {
            const dataLines = lines.slice(1);
            const actions = new Set<string>();
            for (const line of dataLines.slice(0, 20)) { // Check first 20 rows
              const cols = line.split(',');
              const action = (cols[actionIdx] || '').trim().replace(/^"|"$/g, '').toUpperCase();
              if (action) actions.add(action);
            }

            // If all actions are UPDATE ? auto-select update mode
            if (actions.size > 0 && !actions.has('ADD') && actions.has('UPDATE')) {
              setImportMode('update');
              setAutoDetectedMode(true);
            } else if (actions.has('ADD') && !actions.has('UPDATE')) {
              setImportMode('skip');
              setAutoDetectedMode(false);
            } else {
              setAutoDetectedMode(false);
            }
            // If mixed, leave current mode selection to user
          }
        }
      } catch (err) {
        setError(t('extensions.import.invalidCsv'));
        setCsvContent(null);
      }
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!csvContent) return;

    setIsProcessing(true);
    setError(null);
    const startTime = Date.now();

    try {
      const lines = csvContent.split('\n').filter(line => line.trim());
      const totalRows = lines.length > 1 ? lines.length - 1 : 0;

      // For large imports (>1000 rows), use chunked import pattern
      if (totalRows > 1000) {
        // Step 1: Create import job
        const createJobResponse = await fetch(buildApiUrl('/api/telephony/extensions/import/create-job'), {
          method: 'POST',
          headers: getApiHeaders(),
          body: JSON.stringify({
            totalRows,
            mode: importMode,
            csvHeaders: lines[0] // Send the header row
          }),
        });

        if (!createJobResponse.ok) {
          const errorResult = await createJobResponse.json();
          throw new Error(errorResult.message || 'Failed to create import job');
        }

        const jobResult = await createJobResponse.json();
        const { jobId, domainId, headers, headerIndices } = jobResult.data;

        // Step 2: Send chunks of data
        const CHUNK_SIZE = 1000; // 1000 rows per chunk
        const dataLines = lines.slice(1); // Skip header

        for (let i = 0; i < dataLines.length; i += CHUNK_SIZE) {
          const chunk = dataLines.slice(i, i + CHUNK_SIZE);

          const appendResponse = await fetch(buildApiUrl('/api/telephony/extensions/import/append-chunk'), {
            method: 'POST',
            headers: getApiHeaders(),
            body: JSON.stringify({
              jobId,
              domainId,
              headers,
              headerIndices,
              csvLines: chunk
            }),
          });

          if (!appendResponse.ok) {
            const errorResult = await appendResponse.json();
            throw new Error(errorResult.message || 'Failed to upload chunk');
          }
        }

        // Step 3: Commit the import (async - returns 202 immediately)
        const commitResponse = await fetch(buildApiUrl('/api/telephony/extensions/import/commit'), {
          method: 'POST',
          headers: getApiHeaders(),
          body: JSON.stringify({
            jobId,
            domainId,
            mode: importMode
          }),
        });

        const commitResult = await commitResponse.json();

        if (!commitResponse.ok && commitResponse.status !== 202) {
          throw new Error(commitResult.message || 'Failed to commit import');
        }

        // If async (202), poll for completion
        if (commitResult.async) {
          const pollJobId = commitResult.jobId;
          let completed = false;

          while (!completed) {
            await new Promise(resolve => setTimeout(resolve, 2000)); // Poll every 2s

            const statusResponse = await fetch(
              buildApiUrl(`/api/telephony/extensions/import-job/${pollJobId}`),
              { headers: getApiHeaders() }
            );

            if (!statusResponse.ok) continue;

            const statusResult = await statusResponse.json();
            const job = statusResult.data;

            if (job.status === 'completed') {
              completed = true;
              setResults({
                total: job.total_rows || 0,
                created: job.created_count || 0,
                updated: job.updated_count || 0,
                skipped: job.skipped_count || 0,
                deleted: job.deleted_count || 0,
                errors: job.error_details ? [JSON.stringify(job.error_details)] : [],
                elapsedTime: Date.now() - startTime
              });
              setElapsedTime(Date.now() - startTime);
            } else if (job.status === 'failed') {
              completed = true;
              const errorMsg = job.error_details?.message || 'Import job failed';
              throw new Error(errorMsg);
            }
            // else: still processing, continue polling
          }
        } else {
          // Synchronous response (backward compat)
          setResults(commitResult.data);
          setElapsedTime(Date.now() - startTime);
        }

      } else {
        // For smaller imports, use the existing single-request approach
        const endpoint = rowCount > 100
          ? '/api/telephony/extensions/import-bulk'
          : '/api/telephony/extensions/import';

        const response = await fetch(buildApiUrl(endpoint), {
          method: 'POST',
          headers: getApiHeaders(),
          body: JSON.stringify({
            csvContent: csvContent,
            mode: importMode,
          }),
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.message || 'Failed to import extensions');
        }

        setResults(result.data);
        setElapsedTime(Date.now() - startTime);
      }

      // Don't auto-close - let user review results and close manually
    } catch (err) {
      setError(err.message || t('extensions.import.error'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    // If there are results (created, updated, or deleted), refresh the parent data
    if (results && (results.created > 0 || results.updated > 0 || (results.deleted && results.deleted > 0))) {
      onSuccess();
    }

    setSelectedFile(null);
    setCsvContent(null);
    setRowCount(0);
    setResults(null);
    setError(null);
    setImportMode('skip');
    setElapsedTime(null);
    onClose();
  };

  const handleBrowse = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Upload className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">
              {t('extensions.import.title')}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* File Selection */}
          {!results && (
            <>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-foreground">
                  {t('extensions.import.selectFile')}
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <div
                  onClick={handleBrowse}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add('border-primary', 'bg-primary/5');
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-primary', 'bg-primary/5');
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-primary', 'bg-primary/5');
                    const file = e.dataTransfer.files[0];
                    if (file && file.name.endsWith('.csv')) {
                      const event = { target: { files: [file] } } as unknown as React.ChangeEvent<HTMLInputElement>;
                      handleFileSelect(event);
                    }
                  }}
                  className={`
                    relative cursor-pointer rounded-xl border-2 border-dashed 
                    transition-all duration-200 ease-in-out
                    ${selectedFile
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                      : 'border-gray-300 dark:border-gray-600 hover:border-primary hover:bg-primary/5'
                    }
                  `}
                >
                  {selectedFile ? (
                    <div className="py-3 px-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-800/50 flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-green-700 dark:text-green-300">
                            {selectedFile.name}
                          </p>
                          <p className="text-xs text-green-600 dark:text-green-400">
                            {(selectedFile.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile(null);
                          setCsvContent('');
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                        className="text-xs text-red-600 dark:text-red-400 hover:underline"
                      >
                        {t('extensions.import.removeFile') || 'Remove'}
                      </button>
                    </div>
                  ) : (
                    <div className="py-4 px-6 text-center">
                      <div className="mx-auto w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-2">
                        <Upload className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                      </div>
                      <p className="text-sm font-medium text-foreground">
                        {t('extensions.import.dragDrop') || 'Drag & drop your CSV file here'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('extensions.import.orBrowse') || 'or click to browse'}
                      </p>
                      <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:bg-primary/90 transition-colors">
                        <Upload className="h-3 w-3" />
                        {t('extensions.import.browse')}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* File Info */}
              {csvContent && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="space-y-1 text-sm">
                      <p className="font-medium text-blue-900 dark:text-blue-100">
                        {t('extensions.import.fileInfo')}
                      </p>
                      <ul className="text-blue-700 dark:text-blue-300 space-y-1">
                        <li>� {t('extensions.import.totalExtensions')}: {rowCount}</li>
                        <li>� {t('extensions.import.fileFormat')}: CSV</li>
                        <li>� {t('extensions.import.importMethod')}: {rowCount > 100 ? t('extensions.import.bulkImport') : t('extensions.import.standardImport')}</li>
                        <li>� {t('extensions.import.actionColumn') || 'Action Column'}: ADD, UPDATE, DELETE</li>
                        {autoDetectedMode && (
                          <li className="text-green-700 dark:text-green-300 font-medium">
                            ? Mode auto-selected: <strong>Update</strong> (detected from action column)
                          </li>
                        )}
                        {rowCount > 100 && (
                          <li className="text-green-700 dark:text-green-300">
                            ? {t('extensions.import.bulkOptimized')}
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* License Limit Warning */}
              {csvContent && wouldExceedLimit && (
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="space-y-1 text-sm">
                      <p className="font-medium text-amber-900 dark:text-amber-100">
                        {t('extensions.import.licenseLimitWarning') || 'License Limit Warning'}
                      </p>
                      <ul className="text-amber-700 dark:text-amber-300 space-y-1">
                        <li>� {t('extensions.import.currentExtensions') || 'Current extensions'}: {currentExtensions}</li>
                        <li>� {t('extensions.import.maxExtensions') || 'License limit'}: {maxExtensions}</li>
                        <li>� {t('extensions.import.availableSlots') || 'Available slots'}: {availableSlots}</li>
                        <li>� {t('extensions.import.extensionsToImport') || 'Extensions to import'}: {rowCount}</li>
                      </ul>
                      <p className="font-medium text-red-600 dark:text-red-400 pt-2">
                        {t('extensions.import.cannotImport') || `Cannot import ${rowCount} extensions. You can only import ${availableSlots} more extensions.`}
                      </p>
                      {licenseStatus?.mode === 'community' && (
                        <p className="text-amber-600 dark:text-amber-400 pt-1">
                          {t('extensions.import.upgradeLicense') || 'Consider upgrading your license to increase the extension limit.'}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {csvContent && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-foreground">
                    {t('extensions.import.mode')}
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 p-3 border border-border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <input
                        type="radio"
                        name="importMode"
                        value="skip"
                        checked={importMode === 'skip'}
                        onChange={(e) => setImportMode(e.target.value as any)}
                        className="text-primary"
                      />
                      <div>
                        <div className="text-sm font-medium text-foreground">
                          {t('extensions.import.modeSkip')}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {t('extensions.import.modeSkipDesc')}
                        </div>
                      </div>
                    </label>

                    <label className="flex items-center gap-2 p-3 border border-border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <input
                        type="radio"
                        name="importMode"
                        value="update"
                        checked={importMode === 'update'}
                        onChange={(e) => setImportMode(e.target.value as any)}
                        className="text-primary"
                      />
                      <div>
                        <div className="text-sm font-medium text-foreground">
                          {t('extensions.import.modeUpdate')}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {t('extensions.import.modeUpdateDesc')}
                        </div>
                      </div>
                    </label>

                    <label className="flex items-center gap-2 p-3 border border-border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <input
                        type="radio"
                        name="importMode"
                        value="replace"
                        checked={importMode === 'replace'}
                        onChange={(e) => setImportMode(e.target.value as any)}
                        className="text-primary"
                      />
                      <div>
                        <div className="text-sm font-medium text-foreground">
                          {t('extensions.import.modeReplace')}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {t('extensions.import.modeReplaceDesc')}
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              )}
            </>
          )}

          {results && (
            <ImportResultsPanel
              results={results}
              elapsedTime={elapsedTime}
              t={t}
            />
          )}

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-border">
          {!results ? (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-foreground hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                disabled={isProcessing}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleImport}
                disabled={!csvContent || isProcessing || wouldExceedLimit}
                className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t('extensions.import.processing')}
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    {t('extensions.import.import')}
                  </>
                )}
              </button>
            </>
          ) : (
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              {t('common.close')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

